import React, { useEffect, useState } from 'react';
import { fetchUnmatchedWorldpayEvents, UnmatchedWorldpayEvent } from '../api/payments';

const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

// Human-readable "why this needs attention", keyed on the backend's
// reconciliation status (worldpay_service.py's _UNMATCHED_QUEUE_STATUSES).
// Finance cannot act on an undifferentiated list -- each reason implies a
// different next step (chase the reference vs. re-check a settlement vs.
// confirm a guessed match).
const REASON_LABELS: Record<string, string> = {
  unmatched: 'No matching payment found for this reference',
  // Provider-neutral wording (Stripe hardening Task 3 fix round 2): this
  // status is written by BOTH Worldpay ("reference matched") and Stripe
  // ("event matched") settlement attempts -- "reference matched" read
  // oddly on a Stripe row, which has no reference at all.
  settlement_failed: 'Payment matched, but recording it failed',
  flagged_for_reconciliation: 'Matched by amount only — needs manual confirmation',
  // Declined-card visibility (final fix-wave): a non-success event (e.g. the
  // card was refused) used to be logged silently. Staff need to see this so
  // a client who was told "payment taken" but whose card actually failed
  // isn't left thinking they've paid.
  non_success: 'Card declined or payment failed on this payment — no money was taken',
  unmatched_non_success: 'Card declined or payment failed — no matching payment for this reference',
  provider_settlement_failed: 'Worldpay provider settlement failed — needs review',
  // Stripe hardening Task 2: this queue also surfaces Stripe's own
  // unmatched/anomaly_flagged events (tagged provider: 'stripe' below).
  anomaly_flagged: 'Stripe payment resolved to a payment record in an unexpected state — needs review',
};

const reasonLabel = (status?: string): string =>
  (status && REASON_LABELS[status]) || status || 'Needs attention';

/**
 * Read-only surface for the Worldpay webhook's unmatched-events queue
 * (design doc: "surfaced in the payments screen for finance to attach by
 * hand"). Self-contained and read-only: an authorized user gets a visible,
 * retryable error when its request fails, never an indistinguishable empty
 * queue.
 *
 * Final fix-wave (Important 2): a request that SUCCEEDS but carries
 * `stripeQueueError: true` (Stripe's half of the merged queue failed
 * server-side -- see admin.py) is a different case from the one above and
 * is NOT swallowed: in production, where Worldpay ships disabled, Stripe
 * is the only feed into this queue, so silently rendering nothing here
 * would read as "nothing needs attention" when the queue is actually
 * unavailable. This renders a dedicated banner instead, even when
 * `events` is empty.
 */
export const WorldpayUnmatchedQueue: React.FC = () => {
  const [events, setEvents] = useState<UnmatchedWorldpayEvent[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [worldpayQueueError, setWorldpayQueueError] = useState(false);
  const [stripeQueueError, setStripeQueueError] = useState(false);
  const [queueLoadError, setQueueLoadError] = useState(false);

  const load = () => {
    fetchUnmatchedWorldpayEvents()
      .then((data) => {
        setEvents(data?.events || []);
        setWorldpayQueueError(Boolean(data?.worldpayQueueError));
        setStripeQueueError(Boolean(data?.stripeQueueError));
        setQueueLoadError(false);
      })
      .catch(() => {
        setEvents([]);
        setWorldpayQueueError(false);
        setStripeQueueError(false);
        setQueueLoadError(true);
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => { load(); }, []);

  if (!loaded) return null;
  if (events.length === 0 && !worldpayQueueError && !stripeQueueError && !queueLoadError) return null;

  return (
    <div className="admin-payments-feedback error" role="region" aria-label="Unmatched payments">
      {(worldpayQueueError || stripeQueueError || queueLoadError) && (
        <div role="alert">
          {queueLoadError
            ? "The unmatched-payments queue is unavailable right now — retry to check for incidents."
            : worldpayQueueError && stripeQueueError
              ? "Worldpay and Stripe unmatched-payments queues are unavailable right now — events may be missing from this list."
              : worldpayQueueError
                ? "Worldpay's unmatched-payments queue is unavailable right now — some events may be missing from this list."
                : "Stripe's unmatched-payments queue is unavailable right now — some events may be missing from this list."}
        </div>
      )}
      {/* Fix round 1 (Minor 1): Refresh lives OUTSIDE the events.length > 0
          block -- the exact production scenario the stripeQueueError banner
          targets (Worldpay disabled, Stripe half broken) can have ZERO
          events, and staff need a retry control there too, not just a full
          page reload. */}
      <div>
        {events.length > 0 && (
          <strong>{events.length} unmatched payment{events.length === 1 ? '' : 's'}</strong>
        )}
        <button type="button" onClick={load} style={{ marginLeft: '12px' }}>
          {queueLoadError ? 'Retry' : 'Refresh'}
        </button>
      </div>
      {events.length > 0 && (
        <ul>
          {events.map((event) => {
          // Task 2: this queue also carries Stripe rows now (provider tag
          // added server-side); older cached rows have no provider at all,
          // so absence still reads as Worldpay -- this was a Worldpay-only
          // queue before that task.
          const provider = event.provider || 'worldpay';
          // Final fix-wave (Minor 3): an anomaly_flagged/settlement_failed
          // Stripe row DOES have a paymentId (it resolved to a record), but
          // that internal id is useless for the actual next step -- "go
          // look this up in Stripe" needs the Stripe-side locator
          // (sessionId), which log_stripe_event always writes alongside
          // paymentId regardless of status. The old `||` chain stopped at
          // the first truthy value, so a matched Stripe row silently lost
          // its sessionId. Show every locator Stripe rows actually carry;
          // Worldpay rows only ever have transactionReference.
          const locators: Array<[string, string]> = provider === 'stripe'
            ? [
                ...(event.paymentId ? [['payment', event.paymentId] as [string, string]] : []),
                ...(event.sessionId ? [['session', event.sessionId] as [string, string]] : []),
              ]
            : [['ref', event.transactionReference || '—']];
          return (
            <li key={event.id}>
              <strong>{reasonLabel(event.status)}</strong> —{' '}
              {event.amount == null ? 'Amount unavailable' : currencyFormatter.format(event.amount)} —{' '}
              [{provider}]{' '}
              {locators.length > 0
                ? locators.map(([label, value], index) => (
                    <React.Fragment key={label}>
                      {index > 0 && ' — '}
                      {label} <code>{value}</code>
                    </React.Fragment>
                  ))
                : <code>—</code>}
              {event.customerEmail && <> — {event.customerEmail}</>} — {event.eventType} —{' '}
              {new Date(event.receivedAt).toLocaleString('en-GB')}
              {event.message && <div>{event.message}</div>}
            </li>
          );
          })}
        </ul>
      )}
    </div>
  );
};

export default WorldpayUnmatchedQueue;
